import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isAuthenticated, checkAuthStatus } from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { Landing } from '../../pages/Landing';

interface ProtectedRouteProps {
  children: React.ReactNode;
  adminOnly?: boolean; // New prop for admin-only routes
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  adminOnly = false 
}) => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const { user, openLoginModal } = useAuth(); // Get user and openLoginModal from auth context
  const { showToast } = useToast(); // For showing error messages
  
  useEffect(() => {
    const checkAuth = async () => {
      // First check if we're already authenticated via token
      if (isAuthenticated()) {
        setAuthenticated(true);
        // Check if user is admin when adminOnly is true
        if (adminOnly) {
          const user = await checkAuthStatus();
          setIsAdmin(user?.is_superuser || false);
        } else {
          setIsAdmin(true); // Not an admin-only route, so this doesn't matter
        }
        setLoading(false);
        return;
      }
      
      // Then check session-based auth
      try {
        const user = await checkAuthStatus();
        setAuthenticated(!!user);
        setIsAdmin(user?.is_superuser || false);
      } catch (error) {
        console.error('Auth check failed:', error);
        setAuthenticated(false);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };
    
    checkAuth();
  }, [adminOnly]);
  
  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  // If not authenticated, show login modal and render Landing page
  if (!authenticated) {
    // Open the login modal - using a ref to ensure it only runs once
    const modalOpened = React.useRef(false);
    
    useEffect(() => {
      if (!modalOpened.current) {
        openLoginModal();
        modalOpened.current = true;
        console.log("[ProtectedRoute] Opening login modal");
      }
    }, [openLoginModal]);
    
    // Render the Landing page as a background
    return <Landing />;
  }
  
  // If admin-only route but user is not admin, show error and redirect to home
  if (adminOnly && !isAdmin) {
    // Show error toast
    showToast("You don't have permission to access this page", 'error', 5000);
    
    // Redirect to home page
    return <Navigate to="/" replace />;
  }
  
  // User is authenticated and has appropriate permissions
  return <>{children}</>;
};
